import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router';
import type { PageMeta } from './i18n/index.js';

/**
 * Per-route document title, description and route-change announcement
 * (issue #377).
 *
 * WHY A HOOK RATHER THAN A TABLE IN `app.tsx`. The title belongs to the route
 * that owns the content. A central path→title map is a second place a route
 * must be registered, and the failure mode of forgetting is silent: the page
 * renders with the previous route's title. Here a route that does not call
 * `useDocumentMeta` simply has no title of its own, which is visible the first
 * time anyone looks at the tab.
 *
 * WHY A PROVIDER. The route-change ANNOUNCEMENT has to be rendered by the app
 * shell's live region while being produced by a route, and with `React.lazy`
 * the route mounts after the shell has already rendered for that navigation.
 * The provider carries the string from one to the other. It therefore sits
 * INSIDE the router and reads `useLocation`.
 *
 * The first announcement is deliberately suppressed, and focus is not moved:
 * on a fresh page load the document title is already spoken by the browser and
 * the shell does not focus `<main>`, so repeating either is noise. Every LATER
 * route announces.
 *
 * "The first" is the INITIAL LOCATION, not "the first effect to run". An
 * effect-execution counter is replayed by `StrictMode` in development, which
 * turned the initial load into a navigation and announced the page twice
 * (cross-review F004); the location key the provider started on cannot be
 * replayed into a different answer.
 */
interface Announcer {
  readonly announce: (title: string) => void;
  /**
   * True once the reader has navigated away from the location the provider
   * mounted on: the shell skips focus management on the initial load (a fresh
   * document already starts at the top), so the route hook must skip it too.
   */
  readonly hasNavigated: () => boolean;
}

interface AnnouncerValue extends Announcer {
  readonly announcement: string;
}

const AnnouncerContext = createContext<AnnouncerValue | null>(null);

export function DocumentMetaProvider({ children }: { readonly children: ReactNode }) {
  const location = useLocation();
  // The location KEY, not a "first effect run" counter: StrictMode replays the
  // effect on the initial mount, but the key does not change, whereas a second
  // run of a counter looked like a navigation (cross-review F004).
  const [initialKey] = useState(location.key);
  const onInitialLocation = location.key === initialKey;
  // A latch, because a POP back to the initial entry RESTORES its original key:
  // key inequality alone would read coming Back as "the initial load" and drop
  // the announcement and the focus move (cross-review round three, F001). It is
  // written from `hasNavigated`/`announce`, which run in a route's EFFECT, never
  // during render — and that is late enough: the first navigation is already
  // true by key inequality in the render the route mounts in, so only a later
  // return to the initial key needs the latch.
  const leftInitialLocation = useRef(false);
  const hasNavigated = useCallback(() => {
    if (!onInitialLocation) leftInitialLocation.current = true;
    return !onInitialLocation || leftInitialLocation.current;
  }, [onInitialLocation]);
  const [announcement, setAnnouncement] = useState('');
  const announce = useCallback(
    (title: string) => {
      if (!hasNavigated()) return;
      setAnnouncement(title);
    },
    [hasNavigated],
  );
  const value = useMemo<AnnouncerValue>(
    () => ({ announcement, announce, hasNavigated }),
    [announcement, announce, hasNavigated],
  );
  return <AnnouncerContext.Provider value={value}>{children}</AnnouncerContext.Provider>;
}

/** The text the shell's polite live region should read out, or '' for none yet. */
export function usePageAnnouncement(): string {
  return useContext(AnnouncerContext)?.announcement ?? '';
}

/** Finds a `<meta>` by its naming attribute and creates it when absent. */
function writeMeta(name: string, content: string): void {
  if (typeof document === 'undefined') return;
  const selector = `meta[name="${name}"]`;
  let tag = document.head.querySelector<HTMLMetaElement>(selector);
  if (tag === null) {
    tag = document.createElement('meta');
    tag.setAttribute('name', name);
    document.head.append(tag);
  }
  tag.setAttribute('content', content);
}

/**
 * Moves focus onto the active route's `<h1>`, but only when the shell still
 * holds it.
 *
 * A route change focuses `<main>`; when the route is code-split, its content —
 * and therefore its heading — arrives a tick later. This runs from the route's
 * own meta effect, which fires after that content is in the DOM, so the focus
 * lands on the heading the reader should start from.
 *
 * The `activeElement` guard is what stops it stealing focus LATER: a route that
 * updates its title while the reader is several controls deep (knowledge detail
 * resolving its jurisdiction) must not yank them back to the top of the page.
 */
function focusMainHeading(): void {
  if (typeof document === 'undefined') return;
  const main = document.getElementById('main');
  if (main === null) return;
  const active = document.activeElement;
  if (active !== main && active !== document.body) return;
  const heading = main.querySelector('h1');
  if (heading === null) return;
  heading.setAttribute('tabindex', '-1');
  heading.focus();
}

/**
 * Set this route's title and description. Call once from each route component.
 */
export function useDocumentMeta(meta: PageMeta): void {
  const { title, description } = meta;
  const announcer = useContext(AnnouncerContext);
  const announce = announcer?.announce;
  const hasNavigated = announcer?.hasNavigated;
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.title = title;
    writeMeta('description', description);
    // The initial load is not a navigation: the shell does not move focus to
    // `<main>` then, and the heading must not inherit a move the reader never
    // made. Only a LATER route mount moves focus, and only while the shell
    // still holds it.
    if (hasNavigated === undefined || hasNavigated()) focusMainHeading();
    announce?.(title);
  }, [title, description, announce, hasNavigated]);
}
